import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, TrashIcon } from "@heroicons/react/24/outline";
import React, { Fragment } from "react";
import { Button } from "../button";
import { SubmitButton } from "../button/submit";
import { StatusIndicator } from "../statusIndicator";

function SideSheet({
  isOpen,
  onClose,
  title,
  children,
  footerText,
  isAddMode,
  handleSave,
  handleButtonClick,
  size = "standard",
  displayFooter,
  subtitle,
  isForm = true,
  secondaryButtonText,
  secondaryButtonAction,
  primaryButtonDisabled = false,
  secondaryButtonDisabled = false,
  buttonLayout = "auto", // "full" or "auto"
  menuButton, // New prop for menu button
}) {
  let panelWidthClass = "max-w-md";

  if (size === "large") {
    panelWidthClass = "max-w-xl";
  }

  /// Handle the footer ///
  if (!displayFooter && displayFooter !== false) {
    displayFooter = true;
  }

  /// Customize the button text and action ///
  const buttonAction = handleSave || handleButtonClick;
  const buttonLabel = footerText || (!isAddMode ? "Save" : "Add");

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-60" onClose={onClose}>
        <div className="fixed inset-0" />
        <div className="fixed inset-0 overflow-x-visible overflow-y-hidden">
          <div className="absolute inset-0 overflow-x-visible overflow-y-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel
                  className={`pointer-events-auto w-screen ${panelWidthClass}`}
                >
                  <div className="flex h-full flex-col bg-white dark:bg-gray-900 shadow-xl divide-y divide-gray-200 dark:divide-gray-700">
                    <div className="bg-white dark:bg-gray-900 px-4 pt-6 sm:px-6">
                      <div className="pb-4 flex items-center justify-between">
                        <Dialog.Title>
                          <div className="flex items-start flex-col">
                            {subtitle && <StatusIndicator text={subtitle} />}
                            <span className="text-lg font-semibold text-black dark:text-white">
                              {title}
                            </span>
                          </div>
                        </Dialog.Title>
                        <div className="ml-3 flex h-7 items-center space-x-2">
                          {menuButton && (
                            <div className="flex items-center">
                              {menuButton}
                            </div>
                          )}
                          <button
                            type="button"
                            className="rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white"
                            onClick={onClose}
                          >
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto">{children}</div>
                    {displayFooter && (
                      <div className="mt-auto">
                        <div
                          className={`flex flex-shrink-0 px-4 py-4 bg-white dark:bg-gray-900 ${
                            buttonLayout === "auto" &&
                            secondaryButtonText &&
                            secondaryButtonAction
                              ? "justify-between"
                              : buttonLayout === "auto"
                              ? "justify-start"
                              : "justify-between"
                          } items-center`}
                        >
                          {buttonLayout === "auto" ? (
                            // Auto layout - buttons take their natural width
                            <>
                              {buttonAction &&
                                (isForm ? (
                                  <SubmitButton
                                    onClick={buttonAction}
                                    disabled={primaryButtonDisabled}
                                  >
                                    {buttonLabel}
                                  </SubmitButton>
                                ) : (
                                  <Button
                                    onClick={buttonAction}
                                    disabled={primaryButtonDisabled}
                                  >
                                    {buttonLabel}
                                  </Button>
                                ))}
                              {secondaryButtonText && secondaryButtonAction && (
                                <div className="ml-3 mr-3">
                                  <Button
                                    variant="secondary"
                                    icon={TrashIcon}
                                    aria-label={secondaryButtonText}
                                    onClick={secondaryButtonAction}
                                    disabled={secondaryButtonDisabled}
                                  />
                                </div>
                              )}
                            </>
                          ) : (
                            // Full layout - primary button takes full width, secondary on right
                            <>
                              <div className="flex-1">
                                {buttonAction &&
                                  (isForm ? (
                                    <SubmitButton
                                      fullWidth
                                      onClick={buttonAction}
                                      disabled={primaryButtonDisabled}
                                    >
                                      {buttonLabel}
                                    </SubmitButton>
                                  ) : (
                                    <Button
                                      fullWidth
                                      onClick={buttonAction}
                                      disabled={primaryButtonDisabled}
                                    >
                                      {buttonLabel}
                                    </Button>
                                  ))}
                              </div>
                              {secondaryButtonText && secondaryButtonAction && (
                                <div className="ml-3">
                                  <Button
                                    variant="secondary"
                                    icon={TrashIcon}
                                    aria-label={secondaryButtonText}
                                    onClick={secondaryButtonAction}
                                    disabled={secondaryButtonDisabled}
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export { SideSheet };
